import clsx from 'clsx';
import { ReactNode } from 'react';

import styles from './tabs.module.scss';

export type TabItem<TValue extends string> = {
  value: TValue;
  label: ReactNode;
  disabled?: boolean;
  'data-testid'?: string;
};

type Props<TValue extends string> = {
  items: TabItem<TValue>[];
  active: TValue;
  onChange: ( value: TValue ) => void;
  className?: string;
  'data-testid'?: string;
};

/**
 * Folder-tab pattern from Figma 40002692:9893: inactive tabs sit flat against
 * the underline, the active tab lifts above it as a rounded-top card and
 * breaks the line inside its own width.
 *
 * Use the `value` prop to drive selection — the component itself is purely
 * controlled. URL-state syncing or local state is the caller's concern (see
 * SettingsPage for the URL-state recipe).
 */
export function Tabs<TValue extends string>( {
  items,
  active,
  onChange,
  className,
  'data-testid': dataTestId
}: Props<TValue> ) {
  return (
    <div
      className={ clsx( styles.tabs, className ) }
      role="tablist"
      data-testid={ dataTestId }
    >
      { items.map( ( item ) => {
        const isActive = item.value === active;
        return (
          <button
            key={ item.value }
            type="button"
            role="tab"
            aria-selected={ isActive }
            disabled={ item.disabled }
            className={ clsx( styles.tab, isActive && styles.tabActive ) }
            onClick={ () => onChange( item.value ) }
            data-testid={ item['data-testid'] }
          >
            { item.label }
          </button>
        );
      } ) }
    </div>
  );
}
